/**
 * UploadProgressWidget - Shows ongoing uploads in a fixed position
 * 
 * Features:
 * - Fixed position widget (bottom-right corner)
 * - Shows all ongoing uploads
 * - Real-time progress bars
 * - Cancel button for each upload
 * - Collapsible/expandable
 * - Does not block UI
 * - Persists across navigation
 */

import React, { useState } from 'react';
import { useUploadManager } from '../../contexts/UploadManagerContext';
import './UploadProgressWidget.css';

const UploadProgressWidget = () => {
    const { uploads, cancelUpload, clearCompleted } = useUploadManager();
    const [isExpanded, setIsExpanded] = useState(true);

    const uploadList = Object.values(uploads);
    const activeUploads = uploadList.filter(u =>
        u.status === 'uploading' || u.status === 'completing' || u.status === 'pending'
    );
    const completedUploads = uploadList.filter(u => u.status === 'completed');
    const failedUploads = uploadList.filter(u => u.status === 'failed');

    if (uploadList.length === 0) return null;
    
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };
    
    const formatSpeed = (upload) => {
        if (!upload.uploadedBytes || upload.status !== 'uploading') return '';
        
        const elapsed = (Date.now() - upload.startedAt) / 1000; // seconds
        const bytesPerSecond = upload.uploadedBytes / elapsed;
        
        return `${formatFileSize(bytesPerSecond)}/s`;
    };
    
    const formatTimeRemaining = (upload) => {
        if (!upload.uploadedBytes || upload.status !== 'uploading') return '';
        
        const elapsed = (Date.now() - upload.startedAt) / 1000;
        const bytesPerSecond = upload.uploadedBytes / elapsed;
        const remainingBytes = upload.totalBytes - upload.uploadedBytes;
        const remainingSeconds = remainingBytes / bytesPerSecond;
        
        if (remainingSeconds < 60) {
            return `${Math.round(remainingSeconds)}s remaining`;
        } else if (remainingSeconds < 3600) {
            return `${Math.round(remainingSeconds / 60)}m remaining`;
        } else {
            return `${Math.round(remainingSeconds / 3600)}h remaining`;
        }
    };
    
    const getStatusText = (upload) => {
        switch (upload.status) {
            case 'pending':
                return 'Pending...';
            case 'uploading':
                return 'Uploading...';
            case 'completing':
                return 'Finalizing...';
            case 'completed':
                return 'Completed';
            case 'failed':
                return 'Failed';
            case 'aborted':
                return 'Cancelled';
            default:
                return upload.status;
        }
    };
    
    const getStatusIcon = (upload) => {
        switch (upload.status) {
            case 'uploading':
            case 'completing':
                return '⏳';
            case 'completed':
                return '✅';
            case 'failed':
                return '❌';
            case 'aborted':
                return '🚫';
            default:
                return '📁';
        }
    };
    
    return (
        <div className="upload-progress-widget">
            <div className="upload-progress-header" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="upload-progress-title">
                    <span className="upload-progress-icon">📤</span>
                    <span>
                        {activeUploads.length > 0
                            ? `Uploading ${activeUploads.length} file${activeUploads.length !== 1 ? 's' : ''}`
                            : `${completedUploads.length} upload${completedUploads.length !== 1 ? 's' : ''} complete`
                        }
                    </span>
                </div>
                <div className="upload-progress-actions">
                    {completedUploads.length > 0 && (
                        <button
                            className="btn-clear-completed"
                            onClick={(e) => { e.stopPropagation(); clearCompleted(); }}
                            title="Clear completed"
                        >
                            Clear
                        </button>
                    )}
                    <button
                        className="btn-toggle"
                        onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
                    >
                        {isExpanded ? '▼' : '▲'}
                    </button>
                </div>
            </div>

            {isExpanded && (
                <div className="upload-progress-body">
                    {uploadList.map(upload => (
                        <div key={upload.id} className={`upload-item upload-item-${upload.status}`}>
                            <div className="upload-item-header">
                                <div className="upload-item-info">
                                    <span className="upload-status-icon">{getStatusIcon(upload)}</span>
                                    <div className="upload-file-info">
                                        <div className="upload-file-name" title={upload.fileName}>
                                            {upload.fileName}
                                        </div>
                                        <div className="upload-file-meta">
                                            {upload.status === 'uploading' && (
                                                <>
                                                    <span>{formatFileSize(upload.uploadedBytes)} / {formatFileSize(upload.totalBytes)}</span>
                                                    <span className="upload-speed">{formatSpeed(upload)}</span>
                                                    <span className="upload-time-remaining">{formatTimeRemaining(upload)}</span>
                                                </>
                                            )}
                                            {upload.status === 'completed' && (
                                                <span className="upload-success">
                                                    {formatFileSize(upload.totalBytes)} - Upload successful
                                                </span>
                                            )}
                                            {upload.status === 'failed' && (
                                                <span className="upload-error">{upload.error || 'Upload failed'}</span>
                                            )}
                                            {upload.status === 'completing' && <span>Finalizing upload...</span>}
                                            {upload.status === 'pending' && <span>Preparing upload...</span>}
                                            {upload.status === 'aborted' && (
                                                <span className="upload-cancelled">Upload cancelled</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {(upload.status === 'uploading' || upload.status === 'pending' || upload.status === 'completing') && (
                                    <button
                                        className="btn-cancel-upload"
                                        onClick={() => cancelUpload(upload.id)}
                                        title="Cancel upload"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                            {(upload.status === 'uploading' || upload.status === 'pending' || upload.status === 'completing') && (
                                <div className="upload-progress-bar-container">
                                    <div
                                        className="upload-progress-bar"
                                        style={{ width: `${upload.progress}%` }}
                                    >
                                        <span className="upload-progress-text">{upload.progress}%</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default UploadProgressWidget;
